import { customInstance } from "@repo/api-client";

/**
 * Client contract for the custom-domain connector (`/api/v1/venues/{venueId}/custom-domain`,
 * custom-domain.md). Goes through `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`),
 * so a 409 conflict (hostname already connected elsewhere / lost race, R5/R3) and a 403
 * `ENTITLEMENT_REQUIRED` (the paid add-on gate) come back as `ApiError` carrying the RFC 9457 problem.
 * There is NO certificate/file field anywhere — the cert is Cloudflare-managed (R4). Regenerated Orval
 * hooks exist too (`useGetCustomDomain` …); this thin module matches the sibling `siteApi` idiom.
 */

export type CustomDomainStatus = "pending" | "verifying" | "securing" | "live" | "error";

export interface CustomDomainResponse {
  venueId: string;
  hostname: string;
  status: CustomDomainStatus;
  /** The ownership pre-validation TXT record the manager must publish (null until CF registers it). */
  dcvTxtName: string | null;
  dcvTxtValue: string | null;
  /** The in-zone fallback origin the manager CNAMEs their host at. */
  cnameTarget: string | null;
  verifiedAt: string | null;
  lastError: string | null;
  version: number;
}

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/custom-domain`;

const jsonHeaders = { "Content-Type": "application/json" } as const;

export const customDomainApi = {
  /** The venue's connected custom domain + status, or null (204/empty) when none is connected. */
  get: (venueId: string) =>
    customInstance<CustomDomainResponse | null>(base(venueId), { method: "GET" }),

  connect: (venueId: string, hostname: string) =>
    customInstance<CustomDomainResponse>(base(venueId), {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ hostname }),
    }),

  disconnect: (venueId: string) => customInstance<void>(base(venueId), { method: "DELETE" }),
};

/** The `["venue", venueId, "customDomain"]` query-key family this feature owns. */
export const customDomainKeys = {
  domain: (venueId: string) => ["venue", venueId, "customDomain"] as const,
};
